import { useEffect, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { FormProvider, useForm } from 'react-hook-form';
import { CircularProgress } from '@material-ui/core';
import _ from 'lodash';
import NgpTwoButtonDialog from '../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../form/NgpForm';
import FormSection from '../form/FormSection';
import { AUTO_COMPLETE_DEFAULT, MANUAL_ERROR_TYPE, USE_FORM_DEFAULT_OPTIONS } from '../../utilities/FormUtilities';
import NgpTextField from '../form/NgpTextField';
import { INTERNAL_ID_FIELD } from '../form/FormConstants';
import {
    IP_DESCRIPTION_FIELD,
    IP_RULE_FIELD,
    RANGE_SEPARATOR,
    TYPE_IPADDR,
    TYPE_RANGE,
    TYPE_SUBNET,
} from './IpFormConstants';
import IPAddressUtilities from '../../utilities/IPAddressUtilities';

const formId = 'frmIpRules';
const addButtonText = t`Add`;
const updateButtonText = t`Update`;
const uniqueErrorText = t`A rule already exists with this value`;
const defaultFormValues = {};

function cleanupRule(rule) {
    const ips = _.map(rule.valueDisplay.split(RANGE_SEPARATOR), _.trim);
    const [startIp, endIp] = ips;
    const isRange = ips.length === 2;
    const isAddress = IPAddressUtilities.isIPv4Address(startIp);
    const isSubnet = !isRange && !isAddress;

    // essentially we're just removing all spaces
    rule.valueDisplay = _.join(ips, RANGE_SEPARATOR);
    rule.value = {};

    // depending on the type, we have several fields we need to set for the rule
    if (isRange) {
        rule.type = TYPE_RANGE;
        rule.value.start = startIp;
        rule.value.end = endIp;

        return rule;
    }

    if (isSubnet) {
        rule.type = TYPE_SUBNET;
        rule.value.subnet = startIp;

        return rule;
    }

    if (isAddress) {
        rule.type = TYPE_IPADDR;
        rule.value.start = startIp;

        return rule;
    }
}

function validateIP(value) {
    const rangeSeparator = '-';
    const ips = _.map(value.split(rangeSeparator), _.trim);
    const [startIp, endIp] = ips;

    if (ips.length === 2) {
        // this is an IP range. E.g., 192.168.0.0-192.168.0.2
        const v4Addresses = IPAddressUtilities.isIPv4Address(startIp) && IPAddressUtilities.isIPv4Address(endIp);
        const isOrdered = IPAddressUtilities.compareTo(startIp, endIp) === -1;

        return v4Addresses && isOrdered;
    }

    if (ips.length === 1) {
        // normal IP or subnet
        return IPAddressUtilities.isV4(startIp);
    }

    // this is an invalid range, return false.
    return false;
}

const ipRules = {
    validate: (value) => (validateIP(value) ? true : i18n._(t`Please enter a valid IPv4 address, subnet, or range`)),
};

export default function IpScopeForm({
    selectedRecord = {},
    selectedList = [],
    setSelectedRecord,
    open,
    setOpen,
    onConfirm,
}) {
    const methods = useForm({ ...USE_FORM_DEFAULT_OPTIONS, defaultValues: {} });
    const internalId = useRef(null);
    const buttonText = useRef(addButtonText);
    const { handleSubmit, formState, reset, setError } = methods;
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!_.isEmpty(selectedRecord)) {
            internalId.current = selectedRecord[INTERNAL_ID_FIELD];
            buttonText.current = selectedRecord[INTERNAL_ID_FIELD] ? updateButtonText : addButtonText;
            reset(selectedRecord);
        }
    }, [selectedRecord, reset]);

    const handleCleanup = () => {
        internalId.current = null;
        buttonText.current = addButtonText;
        setSelectedRecord(undefined);
        reset(defaultFormValues);
    };

    const handleFormSubmit = async (dataToSave = {}) => {
        if (internalId) {
            _.set(dataToSave, INTERNAL_ID_FIELD, internalId.current);
        }

        if (
            _.filter(selectedList, (listItem) => {
                return (
                    _.toLower(listItem[IP_RULE_FIELD]) === _.toLower(dataToSave[IP_RULE_FIELD]) &&
                    listItem[INTERNAL_ID_FIELD] !== dataToSave[INTERNAL_ID_FIELD]
                );
            }).length > 0
        ) {
            setError(IP_RULE_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: uniqueErrorText,
            });
            return;
        }

        setSaving(true);

        try {
            await onConfirm(cleanupRule(dataToSave));
            handleCleanup();
        } finally {
            setSaving(false);
        }
    };

    const startIcon = saving && <CircularProgress color="inherit" variant="indeterminate" size={16} />;

    return (
        <NgpTwoButtonDialog
            title={<Trans>IP Scope</Trans>}
            confirmButtonText={buttonText.current}
            cancelButtonText={<Trans>Cancel</Trans>}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            confirmButtonDisabled={!formState.isValid || saving}
            confirmButtonProps={{ startIcon }}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleFormSubmit)}
        >
            <FormProvider {...methods}>
                <NgpForm
                    formId={formId}
                    initialized
                    loading={false}
                    showSave={false}
                    formPadding={false}
                    showRequiredFieldsTip={false}
                    submitFunction={handleFormSubmit}
                >
                    <FormSection>
                        <div>
                            <div>
                                <Trans>Note: An IP scope rule must be IPv4 in one of the following formats:</Trans>
                            </div>
                            <ul>
                                <li>
                                    <Trans>IP Address: 0.0.0.0</Trans>
                                </li>
                                <li>
                                    <Trans>IP Subnet: 0.0.0.0/31</Trans>
                                </li>
                                <li>
                                    <Trans>IP Range: 0.0.0.0-0.0.0.255</Trans>
                                </li>
                            </ul>
                        </div>

                        <NgpTextField
                            fieldName={IP_RULE_FIELD}
                            label={i18n._(t`IP Scope`)}
                            required
                            fieldRules={ipRules}
                            inputProps={{
                                size: 65,
                                maxLength: 255,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                        />

                        <NgpTextField
                            fieldName={IP_DESCRIPTION_FIELD}
                            label={i18n._(t`Description`)}
                            inputProps={{
                                size: 65,
                                maxLength: 255,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                        />
                    </FormSection>
                </NgpForm>
            </FormProvider>
        </NgpTwoButtonDialog>
    );
}
